import React from 'react';
import './InputGroup.scss';
import cl from 'classnames';

interface InputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    postfix?: React.ReactNode;
    className?: string;
    ignoreInputStateForHint?: boolean;
}

interface HintTextProps {
    children?: React.ReactNode;
    ignoreInputState?: boolean;
}

/**
 * HintText component for displaying additional information or hints
 * 
 * @param children - Content to display in the hint
 * @param ignoreInputState - If true, color of hint will be independent of input state
 * 
 * @example
 * ```tsx
 * <HintText>This is a hint message</HintText>
 * 
 * <HintText ignoreInputState={true}>
 *   This hint ignores input state styling
 * </HintText>
 * ```
 */
const HintText: React.FC<HintTextProps> = ({ children = null, ignoreInputState = false }) => {
    const hintClassName = ignoreInputState ? "ew-hint-text" : "ew-hint-text-with-input";
    
    return (
        <div className={hintClassName}>
            {children}
        </div>
    );
};

/**
 * InputGroup component for grouping form inputs with optional postfix content
 * 
 * This component provides a flexible way to group form inputs and display
 * additional content (like hints, icons, or buttons) alongside them.
 * 
 * @param children - Form input elements to group together
 * @param postfix - Optional content to display after the input group (renders as HintText)
 * @param className - Additional CSS classes to apply to the input group
 * @param ignoreInputStateForHint - If true, the postfix hint will ignore input state styling
 * @param rest - Additional HTML div attributes
 * 
 * @example
 * ```tsx
 * // Basic input group
 * <InputGroup>
 *   <Input type="text" placeholder="Enter your name" />
 * </InputGroup>
 * 
 * // Input group with hint text
 * <InputGroup postfix="Maximum 50 characters">
 *   <Input type="text" placeholder="Enter your name" maxLength={50} />
 * </InputGroup>
 * 
 * // Input group with custom styling and hint
 * <InputGroup 
 *   className="custom-input-group"
 *   postfix="Required field"
 *   ignoreInputStateForHint={true}
 * >
 *   <Input type="email" placeholder="Enter your email" required />
 * </InputGroup>
 * 
 * // Input group with multiple inputs
 * <InputGroup postfix="Both fields are required">
 *   <Input type="text" placeholder="First name" />
 *   <Input type="text" placeholder="Last name" />
 * </InputGroup>
 * ```
 */
const InputGroup: React.FC<InputGroupProps> = ({
    children,
    postfix,
    className = '',
    ignoreInputStateForHint = false,
    ...rest
}) => {
    const [postfixInitial] = React.useState(postfix);
    const inputGroupClassName = cl("ew-input-group", className);
    const inputGroup = (
        <div className={inputGroupClassName} {...rest}>
            {children}
        </div>
    );
    if((postfixInitial === undefined && postfix !== undefined)
        || (postfixInitial !== undefined && postfix === undefined)) {
        console.warn(`InputGroup postfix type changed from inital one, this may cause unexpected behavior with input focus. Postfix should be always undefined or always defined(null is allowed)`);
    }

    if (postfix !== undefined) {
        return (
            <div className="ew-input-group--with-postfix">
                {inputGroup}
                <HintText ignoreInputState={ignoreInputStateForHint}>
                    {postfix}
                </HintText>
            </div>
        );
    }

    return inputGroup;
};


export default InputGroup;